import { useAuthentication } from '@context/businessContainer';
import Link from 'next/link';
import Button from '@monofront/design-system/components/Button';
import HeartLoader from '@monofront/design-system/components/HeartLoader';
import { useProceedCheckout } from '@components/ProceedCheckout/useProceedCheckout';
import useNavigationLoader from '@hooks/useNavigationLoader';
import { useSubscribeInCheckout } from '@monofront/business/hooks/checkout/useSubscribeInCheckout';
import { RoutesEnum } from '@monofront/types';
import { useShoppingCart } from '@hooks/cart/useShoppingCart';
import { useTranslations } from 'next-intl';
import useLocalizedRoutes from '@hooks/i18n/useLocalizedRoutes';
import { useCartMaxWeight } from '@hooks/useCartMaxWeight';
import useMinimumOrderAmount from '@hooks/useMinimumOrderAmount';

interface IProceedCheckoutProps {
  paymentButtonLabel: string;
  disabledButton: boolean;
  dataTestId: string;
}

const ProceedCheckout: React.FC<IProceedCheckoutProps> = ({
  paymentButtonLabel,
  disabledButton,
  dataTestId,
}) => {
  const { isLoading: isNavigationLoading } = useNavigationLoader();

  const { authUser, isUserReady } = useAuthentication();
  const { isSubscriptionAvailable } = useSubscribeInCheckout(useAuthentication);

  const { shoppingCart } = useShoppingCart();
  const hasOnlyGiftItem = Boolean(
    shoppingCart.hasGiftItem && shoppingCart.products.length === 1,
  );
  const { getPath } = useLocalizedRoutes();
  const maxWeight = useCartMaxWeight();

  const tCart = useTranslations('Cart');
  const { minimumOrderAmount } = useMinimumOrderAmount();

  const {
    proceedCheckout,
    minimumOrderAmountNotReached,
    isLoading: isProceedCheckoutLoading,
  } = useProceedCheckout();

  const isLoading =
    isProceedCheckoutLoading || isNavigationLoading || !isUserReady;

  return (
    <>
      {hasOnlyGiftItem ? (
        <Link href={getPath(RoutesEnum.ESSENTIALS_COLLECTION)} legacyBehavior>
          <a data-testid="ProceedCheckout-go-to-complete-cart">
            <Button hasMinWidth={false}>{tCart('iCompleteTheCart')}</Button>
          </a>
        </Link>
      ) : authUser ? (
        isLoading ? (
          <div className="heart-container">
            <HeartLoader />
          </div>
        ) : (
          <Button
            hasMinWidth={false}
            disabled={disabledButton}
            onClick={proceedCheckout}
            dataTestId={dataTestId}
          >
            {paymentButtonLabel}
          </Button>
        )
      ) : shoppingCart.totalPrice < minimumOrderAmount &&
        isSubscriptionAvailable ? (
        <Button
          hasMinWidth={false}
          disabled={shoppingCart.totalWeight >= maxWeight}
          onClick={minimumOrderAmountNotReached}
          data-testid={dataTestId}
        >
          {tCart('validateAndPay')}
        </Button>
      ) : (
        <Link
          href={
            isSubscriptionAvailable
              ? '/checkout/register'
              : '/account/register/flow'
          }
          legacyBehavior
        >
          <a
            className="start-subscription"
            rel="nofollow"
            data-testid={dataTestId}
          >
            <Button
              hasMinWidth={false}
              disabled={shoppingCart.totalWeight >= maxWeight}
            >
              {isSubscriptionAvailable ? (
                tCart('validateAndPay')
              ) : (
                <>{tCart('startTrialAndContinue')}</>
              )}
            </Button>
          </a>
        </Link>
      )}
      <style jsx>{`
        .member-link {
          margin-bottom: var(--space-3);
          text-align: center;
        }

        .heart-container :global(.beating-heart) {
          margin: auto auto;
        }

        a :global(button) {
          white-space: nowrap;
          line-height: initial;
        }
        @media (max-width: 800px) {
          .start-trial :global(button),
          .start-subscription :global(button) {
            white-space: initial;
          }
        }
      `}</style>
    </>
  );
};

export default ProceedCheckout;
